import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/contexts/AuthContext';
import { X, User, Users, ShieldCheck, AlertCircle, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

/**
 * SafeMeetCancelSheet
 * Verbatim port of the mobile `SafeMeetCancelSheet.tsx` to web.
 * Dark glassmorphism modal — 4 reasons, penalty-free badges,
 * server-classified cancel_type (do NOT send from client).
 */
export default function SafeMeetCancelSheet({ open, meetId, onClose, onCancelled }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('personal');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) { setReason('personal'); setError(''); setSubmitting(false); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const submit = async () => {
    setError(''); setSubmitting(true);
    try {
      const res = await api.put(`/safe-meet/${meetId}/cancel`, { cancel_reason: reason });
      onCancelled?.(res.data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't cancel — try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const REASONS = [
    { key: 'personal',       icon: User,         label: t('safeMeetCancel.reasonPersonal'),      penaltyFree: false },
    { key: 'mutual',         icon: Users,        label: t('safeMeetCancel.reasonMutual'),        penaltyFree: true  },
    { key: 'safety',         icon: ShieldCheck,  label: t('safeMeetCancel.reasonSafety'),        penaltyFree: true  },
    { key: 'provider_error', icon: AlertCircle,  label: t('safeMeetCancel.reasonProviderError'), penaltyFree: true  },
  ];
  const activeIsPenaltyFree = REASONS.find(r => r.key === reason)?.penaltyFree;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-5"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Cancel meet"
      data-testid="safemeet-cancel-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-3xl overflow-hidden relative"
        style={{
          boxShadow: '0 20px 60px -20px rgba(220,38,38,0.55), 0 0 0 1px rgba(239,68,68,0.28)',
        }}
      >
        {/* Dark glass background with red-tint gradient */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(220,38,38,0.16) 0%, rgba(15,15,15,0.96) 40%, rgba(15,15,15,0.96) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }}
        />
        {/* Content */}
        <div className="relative px-6 pt-7 pb-6 flex flex-col items-center gap-4">
          {/* Icon ring */}
          <div
            className="w-[60px] h-[60px] rounded-full flex items-center justify-center"
            style={{ background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.4)' }}
          >
            <div
              className="w-[52px] h-[52px] rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)' }}
            >
              <X size={26} strokeWidth={2.5} className="text-white" />
            </div>
          </div>

          <div className="text-center">
            <h2 className="text-white font-black" style={{ fontSize: 20, letterSpacing: '-0.01em' }}>{t('safeMeetCancel.title')}</h2>
            <p className="mt-1.5" style={{ color: 'rgba(255,255,255,0.82)', fontSize: 14, lineHeight: 1.45 }}>
              {t('safeMeetCancel.sub')}
            </p>
          </div>

          {/* Reasons */}
          <div className="w-full flex flex-col gap-2 mt-1">
            {REASONS.map((r) => {
              const active = reason === r.key;
              const Icon = r.icon;
              return (
                <button
                  key={r.key}
                  type="button"
                  role="option"
                  aria-selected={active}
                  data-testid={`safemeet-cancel-reason-${r.key}`}
                  onClick={() => setReason(r.key)}
                  className="w-full flex items-center gap-3 rounded-xl transition-all"
                  style={{
                    padding: '12px 14px',
                    border: `1px solid ${active ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.12)'}`,
                    background: active ? 'rgba(239,68,68,0.14)' : 'rgba(255,255,255,0.04)',
                  }}
                >
                  <Icon
                    size={18}
                    className={active ? 'text-white' : 'text-white/70'}
                  />
                  <span
                    className="flex-1 text-left"
                    style={{
                      color: active ? '#FFFFFF' : 'rgba(255,255,255,0.85)',
                      fontSize: 14.5,
                      fontWeight: active ? 700 : 600,
                    }}
                  >
                    {r.label}
                  </span>
                  {r.penaltyFree && <CheckCircle2 size={14} style={{ color: '#22C55E' }} />}
                </button>
              );
            })}
          </div>

          {activeIsPenaltyFree && (
            <p className="text-center" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
              {t('safeMeetCancel.penaltyFreeHelper')}
            </p>
          )}
          {error && (
            <p className="text-center" style={{ color: '#FCA5A5', fontSize: 12 }}>{error}</p>
          )}

          {/* Confirm button */}
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="safemeet-cancel-confirm"
            className="w-full rounded-2xl flex items-center justify-center gap-2 mt-1"
            style={{
              background: submitting
                ? 'linear-gradient(90deg, #525252 0%, #404040 50%, #262626 100%)'
                : 'linear-gradient(90deg, #F87171 0%, #DC2626 50%, #991B1B 100%)',
              padding: '15px 20px',
              boxShadow: submitting ? 'none' : '0 8px 24px -8px rgba(220,38,38,0.6)',
            }}
          >
            {submitting ? (
              <Loader2 size={18} className="text-white animate-spin" />
            ) : (
              <XCircle size={18} className="text-white" />
            )}
            <span className="text-white" style={{ fontSize: 15, fontWeight: 800, letterSpacing: 0.2 }}>
              {submitting ? t('safeMeet.cancellingBtn') : t('safeMeetCancel.confirmBtn')}
            </span>
          </button>

          {/* Keep meet */}
          <button
            type="button"
            onClick={onClose}
            data-testid="safemeet-keep"
            className="mt-0.5"
            style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, fontWeight: 600 }}
          >
            {t('safeMeetCancel.keepBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}
